// @givmo/employer-module — sign-up EMAIL+PASSWORD screen (/sign-up/email-password).
// Ports `sign_up_email_password_screen.dart`.
//
// Work email + password (with the 3 LIVE password-rule indicators) + the inline "Already have a
// Givmo app account with your work email? Log-in" branch (the host's word for it; → the host's sign-in
// with the redirect block).
//
// ★ NF-FIX21: THIS SCREEN NO LONGER REGISTERS THE COMPANY. On submit it first asks the backend whether
// the address can only fail the company create in a way the details cannot fix — a personal-email
// domain, or (join-or-create off) a domain that already has a company — and shows the create's own
// words for it here, before any account exists (`company-email-precheck.ts`). Otherwise @givmo/auth
// `adminSignUpWithEmail` creates the Firebase user, sets the display name and provisions the Givmo
// user row — rolling the Firebase user back if that fails, so an address already on a Givmo account
// still fails here with nothing left behind. On success → the OTP screen marked `?register=true`, which
// proves the work email and THEN registers the company from the carried details. Registering first,
// as this screen did until NF-FIX21, let a sign-up abandoned at the code screen hold a real company's
// domain. The join pivot (EP-SUITE2) is decided by that registration now, after the code.
// An `emailAlreadyInUse` (Flutter `UserAlreadyExists`) routes to the host's sign-in WITH the redirect
// block. Normal disabled-until-valid button (the always-tappable Flutter workaround dropped; the
// VALIDATION LOGIC preserved).
//
// Rendered by both hosts (EP-MNT8): their auth, addresses, look and frame come from `useSignUpHost()`.

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Form,
  FormField,
  TextField,
  ErrorMessage,
  Icon,
  useGivmoForm,
  testIdProp,
} from '@givmo/design-system';
import { Check, Circle } from 'lucide-react';
import { AuthError } from '@givmo/auth';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { SignUpHeading, useSignUpHost } from './sign-up-host';
import type { SignUpLook } from './sign-up-look';
import { useAuthError } from './use-auth-error';
import { useSignUpDetails } from './sign-up-context';
import { companyEmailEarlyRefusal } from './company-email-precheck';
import {
  signUpEmailPasswordSchema,
  INVALID_EMAIL_MESSAGE,
  passwordHasMinLength,
  passwordHasLettersAndNumbers,
  passwordHasSymbol,
  type SignUpEmailPasswordFormValues,
} from './auth-schemas';

function PasswordRule({
  met,
  label,
  testId,
  look,
}: {
  met: boolean;
  label: string;
  testId: string;
  look: SignUpLook;
}) {
  return (
    <div className={look.passwordRule(met)} data-checked={met || undefined} {...testIdProp(testId)}>
      <Icon icon={met ? Check : Circle} size={16} aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function SignUpEmailPasswordScreen() {
  const { auth, paths, look, words, Shell } = useSignUpHost();
  const { signUpWithEmail, user } = auth;
  const navigate = useNavigate();
  const { details, adopt } = useSignUpDetails(user?.uid ?? null);
  // EP-SUITE2-FIX6: the sign-up creates the account before it can fail (and rolls it back when it
  // does), so the error must outlive the identity boundary's remount — the door's key.
  const { error, clearError, surface } = useAuthError('sign-up-email-password');
  const [submitting, setSubmitting] = useState(false);

  const form = useGivmoForm(signUpEmailPasswordSchema, {
    defaultValues: { email: '', password: '' },
    mode: 'onTouched',
  });
  // Live password value drives the 3 indicators (independent of the submit gate).
  const password = form.watch('password') ?? '';

  const onSubmit = async (values: SignUpEmailPasswordFormValues) => {
    clearError();
    // The details screen must have run first (it gates entry here in the email path). Defensive:
    // bounce back if the carry-over is missing.
    if (details === null) {
      await navigate({ to: paths.signUpDetails });
      return;
    }
    setSubmitting(true);
    try {
      // NF-FIX21: what the company create would refuse whatever the details said fails HERE, before
      // any account exists — with the create's own words (fail-open: `null` proceeds).
      const refusal = await companyEmailEarlyRefusal(values.email, isCompanyJoinOrCreateEnabled());
      if (refusal !== null) {
        surface(new AuthError('unknown', { message: refusal }));
        return;
      }
      const { credential } = await signUpWithEmail({
        email: values.email,
        password: values.password,
        firstName: details.firstName,
        lastName: details.lastName,
      });
      // The account this sign-up created takes the details its visitor typed signed out — the one way
      // they cross a sign-in (EP-MNT8-FIX1, `sign-up-context.tsx`), so its code screen registers them.
      adopt(credential.user.uid);
      // The account and its row exist, with NO company: prove the work email, then register.
      await navigate({ to: paths.signUpOtp, search: { register: true } });
    } catch (thrown) {
      const authError = surface(thrown);
      if (authError.kind === 'emailAlreadyInUse') {
        // Existing Givmo app account on this work email → sign in with the redirect block.
        await navigate({ to: paths.signIn, search: { 'redirect-block': true } });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell testId="page-sign-up-email-password" wide>
      <button
        type="button"
        onClick={() => void navigate({ to: paths.signUp })}
        className={look.textButton}
        {...testIdProp('auth-signup-emailpw-back')}
      >
        Back
      </button>

      <SignUpHeading heading={<h1 className={look.heading}>Create your admin account</h1>} />

      <Form form={form} onSubmit={onSubmit} testId="auth-signup-emailpw-form">
        <FormField
          name="email"
          render={({ field, invalid }) => (
            <TextField
              label="Work email"
              type="email"
              autoComplete="email"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={invalid}
              errorMessage={INVALID_EMAIL_MESSAGE}
              testId="auth-signup-email-input"
            />
          )}
          hideError
        />

        <p className={look.body}>
          Already have a Givmo app account with your work email?{' '}
          <button
            type="button"
            onClick={() => void navigate({ to: paths.signIn, search: { 'redirect-block': true } })}
            className={look.inlineLink}
            {...testIdProp('auth-signup-login-link')}
          >
            {words.signIn}
          </button>
        </p>

        <FormField
          name="password"
          render={({ field, invalid }) => (
            <TextField
              label="Create your password"
              type="password"
              autoComplete="new-password"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={invalid}
              testId="auth-signup-password-input"
            />
          )}
          hideError
        />

        <div className="flex flex-col gap-8">
          <PasswordRule
            met={passwordHasMinLength(password)}
            label="Has at least 8 characters"
            testId="auth-password-rule-length"
            look={look}
          />
          <PasswordRule
            met={passwordHasLettersAndNumbers(password)}
            label="Has both letters and numbers"
            testId="auth-password-rule-alnum"
            look={look}
          />
          <PasswordRule
            met={passwordHasSymbol(password)}
            label="Has at least one symbol"
            testId="auth-password-rule-symbol"
            look={look}
          />
        </div>

        {error !== null && <ErrorMessage testId="auth-error">{error}</ErrorMessage>}

        <Button
          type="submit"
          {...look.primary}
          loading={submitting}
          disabled={!form.formState.isValid || submitting}
          testId="auth-signup-create-account"
        >
          Create account
        </Button>
      </Form>
    </Shell>
  );
}
